/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  ConditionType,
  Currency,
  CurrencyType,
  PlayerType,
  RegisteredPlayer,
  Registration,
  Team,
  TeamOfficialType,
} from '@sportninja/common/types/Registration'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { Alert, App } from 'antd'
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  Suspense,
  useEffect,
} from 'react'
import { useSearchAndFilter } from 'src/hooks/useSearchAndFilter'
import ApproveWithConditionsModal from './ApproveWithConditionsModal'
import DivisionTable from './DivisionTable'
import { EmptySearch } from './EmptySearch'
import { useTableColumns } from './helpers/divisionTableHelper'
import { SearchControls } from './SearchControls'
import colors from '@sportninja/common/constants/appColors'
import { WarningFilled, LoadingOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { RegistrationTableDivision } from 'src/pages/Schedule/PlayerRegistration/helpers'
import Text from '../Text'
import { ROUTES } from '@sportninja/common/constants/app'
import { Country } from '@sportninja/common/types/Country'
import DeleteModal from 'src/components/Modal/DeleteModal'
import req from '@sportninja/common/api/request'
import { Link } from 'react-router-dom'
import { RegistrationStatus } from '@sportninja/common/constants/RegistrationStatus'
import PaidInPersonAlertModal from './PaidInPersonAlertModal'
import CancelRegistrationModal from './CancelRegistrationModal'

const checkForDeferPayment = (entity: any) => {
  return entity?.defer_payment || false
}

const AdminPlayerRegisterModal = React.lazy(
  () =>
    import(
      'src/pages/Schedule/PlayerRegistration/components/AdminPlayerRegisterModal'
    )
)
const AdminTeamRegisterModal = React.lazy(
  () =>
    import(
      'src/pages/Schedule/PlayerRegistration/components/AdminTeamRegisterModal'
    )
)
const BucketModal = React.lazy(() => import('./DivisionTable/BucketModal'))

const ManagePlayersModal = React.lazy(
  () => import('./DivisionTable/PlayerDetailsTable/ManagePlayersModal')
)

const BillingScheduleModal = React.lazy(
  () => import('src/pages/Schedule/PlayerRegistration/BillingScheduleModal')
)

const registrationOptions = [
  {
    label: 'Submitted',
    value: 1,
  },
  {
    label: 'Pending',
    value: 2,
  },
  {
    label: 'Approved',
    value: 3,
  },
  {
    label: 'Approved w/ Conditions',
    value: 4,
  },
  {
    label: 'Cancelled',
    value: 5,
  },
]

const DeleteBucketButton = ({ ...props }) => (
  <div id='delete-bucket-button' {...props} />
)

type RegistrationTableProps = {
  showWaiver: boolean
  registrationVersion: number
  playerTypes: PlayerType[]
  divisions: RegistrationTableDivision[]
  setDivisions: React.Dispatch<
    React.SetStateAction<RegistrationTableDivision[]>
  >
  onPressDivisionSetup: (division: RegistrationTableDivision) => void
  scheduleId: string
  updateTeamRegistrationStatus: (props: any) => void
  conditionType: ConditionType[]
  refetch: (force?: boolean) => void
  currencies: CurrencyType[]
  registration: Registration | null | undefined
  teamOfficialTypes: TeamOfficialType[]
  setDivisionToDelete: (division: RegistrationTableDivision) => void
  setIsDivisionCloneOpen: (isOpen: boolean) => void
  setDivisionToClone: (division: RegistrationTableDivision) => void
  setIsRegistrationDetailsModalOpen: (isOpen: boolean) => void
  setRegistrationDetailId: (id: string) => void
  setRegistrationDetailDivision: (
    division: RegistrationTableDivision | null
  ) => void
  setRegistrationDetailDefaultTab: (key: '0' | '1') => void
  updatePlayerRegistrationStatus: (props: any) => void
  countries: Country[]
  fetchAnalytics: () => void
  isPlayerRegistrationEnabled: boolean
  isTeamRegistrationEnabled: boolean
}

const getUpdateMessage = (entity: string, status: string) => {
  switch (status) {
    case 'Submitted':
      return `${entity} has been Submitted.`
    case 'Pending':
      return `${entity} is now Pending.`
    case 'Approved':
      return `${entity} has been Approved. They can now participate in the competition.`
    case 'Approved With Conditions':
      return `${entity} has been Approved With Conditions. They can now participate in the competition.`
    case 'Cancelled':
      return `${entity} has been Cancelled. They have been removed from the the competition.`
    default:
      return `${entity} has been ${status}.`
  }
}

const RegistrationTable = ({
  divisions,
  setDivisions,
  onPressDivisionSetup,
  scheduleId,
  updateTeamRegistrationStatus,
  updatePlayerRegistrationStatus,
  conditionType,
  currencies,
  refetch,
  registration,
  teamOfficialTypes,
  setDivisionToDelete,
  setIsDivisionCloneOpen,
  setDivisionToClone,
  setIsRegistrationDetailsModalOpen,
  setRegistrationDetailId,
  setRegistrationDetailDivision,
  setRegistrationDetailDefaultTab,
  countries,
  fetchAnalytics,
  playerTypes,
  isPlayerRegistrationEnabled,
  isTeamRegistrationEnabled,
  registrationVersion = 1,
  showWaiver = false,
}: RegistrationTableProps) => {
  const { message } = App.useApp()
  const [adminTeamRegisterModalOpen, setAdminTeamRegisterModalOpen] =
    useState(false)
  const [loadingTeamIds, setLoadingTeamIds] = useState<string[]>([])
  const [loadingPlayerIds, setLoadingPlayerIds] = useState<string[]>([])
  const [teamToCancel, setTeamToCancel] = useState<any>(null)
  const [playerToCancel, setPlayerToCancel] = useState<any>(null)
  const [currentDivision, setCurrentDivision] = useState<
    RegistrationTableDivision | null | undefined
  >(null)
  const [isBillingScheduleModalOpen, setIsBillingScheduleModalOpen] =
    useState(false)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [selectedPlayer, setSelectedPlayer] = useState<RegisteredPlayer | null>(
    null
  )
  const [selectedPlayerTeamId, setSelectedPlayerTeamId] = useState<
    string | null
  >(null)
  const [isBucketModalOpen, setIsBucketModalOpen] = useState(false)
  const [isPlayerAdminModalOpen, setIsPlayerAdminModalOpen] = useState(false)
  const [parentTeamRegistrationId, setParentTeamRegistrationId] = useState<
    string | null
  >(null)
  const [teamId, setTeamId] = useState<string | null>(null)
  const [teamName, setTeamName] = useState<string | null>(null)
  const [divisionId, setDivisionId] = useState<string | null>(null)
  const [division, setDivision] = useState<RegistrationTableDivision | null>(
    null
  )
  const [scheduleRegistrationId, setScheduleRegistrationId] = useState<
    string | null
  >(null)
  const [bucketScheduleRegistrationId, setBucketScheduleRegistrationId] =
    useState<string | null>(null)
  const [bucketToEdit, setBucketToEdit] = useState<any>(null)
  const [bucketToDelete, setBucketToDelete] = useState<any>(null)
  const [isManagePlayersModalOpen, setIsManagePlayersModalOpen] =
    useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedPlayers, setSelectedPlayers] = useState<RegisteredPlayer[]>([])
  const handleClearSelection = useCallback(() => {
    setSelectedRowKeys([])
  }, [])

  const openApproveWithConditionsModal = useCallback(
    (
      entity: Team | RegisteredPlayer,
      divisionId: string,
      scheduleRegistrationId: string
    ) => {
      console.log(
        '🔄 RegistrationTable: Opening approve with conditions modal',
        { entity, divisionId }
      )
      setApproveWithConditionsEntity(entity)
      setApproveWithConditionsDivisionId(divisionId)
      setApproveWithConditionsScheduleRegistrationId(scheduleRegistrationId)
      setIsApproveWithConditionsModalOpen(true)
    },
    []
  )

  const closeApproveWithConditionsModal = useCallback(() => {
    setIsApproveWithConditionsModalOpen(false)
    setApproveWithConditionsEntity(null)
    setApproveWithConditionsDivisionId(null)
    setApproveWithConditionsScheduleRegistrationId(null)
  }, [])
  // Use Set for O(1) lookup performance on expandedRows
  const [expandedRowsSet, setExpandedRowsSet] = useState<Set<string>>(new Set())
  const expandedRows = useMemo(
    () => Array.from(expandedRowsSet),
    [expandedRowsSet]
  )
  const hasCalledExpandOnMount = useRef(false)

  // Performance optimization: Track if we have large dataset
  const isLargeDataset = useMemo(() => {
    const totalTeams = divisions.reduce(
      (acc, div) => acc + (div.teams?.length || 0),
      0
    )
    const totalPlayers = divisions.reduce(
      (acc, div) =>
        acc +
        (div.teams?.reduce(
          (pAcc, team) => pAcc + (team.registered_players?.length || 0),
          0
        ) || 0),
      0
    )
    return totalTeams > 50 || totalPlayers > 200
  }, [divisions])

  // Helper function to update expandedRowsSet efficiently
  const setExpandedRows = useCallback(
    (updater: string[] | ((prev: string[]) => string[])) => {
      setExpandedRowsSet((prev) => {
        const newRows =
          typeof updater === 'function' ? updater(Array.from(prev)) : updater
        return new Set(newRows)
      })
    },
    []
  )
  const [isPaidInPersonAlertOpen, setIsPaidInPersonAlertOpen] = useState(false)
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    value: number
    teamId?: string
    team?: any
    playerId?: string
    player?: any
    divisionId?: string
    entityType: 'team' | 'player'
  } | null>(null)
  const [paidInPersonError, setPaidInPersonError] = useState<string | null>(
    null
  )
  const [isPaidInPersonLoading, setIsPaidInPersonLoading] = useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [cancelModalError, setCancelModalError] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [
    isApproveWithConditionsModalOpen,
    setIsApproveWithConditionsModalOpen,
  ] = useState(false)
  const [approveWithConditionsEntity, setApproveWithConditionsEntity] =
    useState<Team | RegisteredPlayer | null>(null)
  const [approveWithConditionsDivisionId, setApproveWithConditionsDivisionId] =
    useState<string | null>(null)
  const [
    approveWithConditionsScheduleRegistrationId,
    setApproveWithConditionsScheduleRegistrationId,
  ] = useState<string | null>(null)

  // Pagination state for large datasets
  const [visibleDivisionCount, setVisibleDivisionCount] = useState(10)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const searchAndFilterProps = useSearchAndFilter({
    divisions,
    setExpandedRows,
  })

  const {
    searchText,
    setSearchText,
    selectedDivision,
    setSelectedDivision,
    getFilteredTeams,
    getFilteredDivisions,
    openDivisions,
    setOpenDivisions,
    allExpanded,
    setAllExpanded,
    getFilteredPlayers,
    onlyBuckets,
    setOnlyBuckets,
    onlyDeferredPayments,
    setOnlyDeferredPayments,
    selectedStatuses,
    setSelectedStatuses,
    shouldExpandOnMount,
  } = searchAndFilterProps

  // Memoize filtered divisions to prevent unnecessary recalculations
  const filteredDivisions = useMemo(() => {
    const filtered = getFilteredDivisions()
    // For large datasets, limit initial display
    if (isLargeDataset && !searchText && filtered.length > 10) {
      return filtered.slice(0, 10)
    }
    return filtered
  }, [getFilteredDivisions, isLargeDataset, searchText])

  // Apply pagination to filtered divisions for better performance
  const visibleDivisions = useMemo(() => {
    // If searching, show all results
    if (searchText || selectedDivision || selectedStatuses.length > 0) {
      return filteredDivisions
    }
    // Otherwise paginate for performance
    return filteredDivisions.slice(0, visibleDivisionCount)
  }, [
    filteredDivisions,
    visibleDivisionCount,
    searchText,
    selectedDivision,
    selectedStatuses,
  ])

  const hasMoreDivisions = visibleDivisions.length < filteredDivisions.length

  // Load more divisions handler
  const handleLoadMore = useCallback(() => {
    setIsLoadingMore(true)
    // Use setTimeout to show loading state and prevent UI freeze
    setTimeout(() => {
      setVisibleDivisionCount((prev) =>
        Math.min(prev + 10, filteredDivisions.length)
      )
      setIsLoadingMore(false)
    }, 100)
  }, [filteredDivisions.length])

  // Call toggleAllDivisions on mount if shouldExpandOnMount is true
  useEffect(() => {
    if (
      shouldExpandOnMount &&
      !hasCalledExpandOnMount.current &&
      divisions.length > 0
    ) {
      toggleAllDivisions()
      hasCalledExpandOnMount.current = true
    }
  }, [shouldExpandOnMount, divisions])

  // Toggles expansion state of all divisions at once
  // Optimized with memoization and batch updates
  const toggleAllDivisions = useCallback(() => {
    // Batch state updates for better performance
    requestAnimationFrame(() => {
      // Set all divisions open/closed state at once
      setOpenDivisions(
        divisions.reduce((acc, _, index) => {
          acc[index] = !allExpanded
          return acc
        }, {} as { [key: number]: boolean })
      )

      if (!allExpanded) {
        // Use Set for deduplication and faster operations
        const teamsRosterIds = new Set<string>()
        divisions.forEach((division) => {
          division.teams?.forEach((team) => {
            if (team.team_roster_id) {
              teamsRosterIds.add(team.team_roster_id)
            }
          })
        })
        setExpandedRows(Array.from(teamsRosterIds))
      } else {
        setExpandedRows([])
      }

      setAllExpanded(!allExpanded)
    })
  }, [
    allExpanded,
    divisions,
    setExpandedRows,
    setOpenDivisions,
    setAllExpanded,
  ])

  // Update openDivisions when search text changes
  React.useEffect(() => {
    if (searchText) {
      setOpenDivisions(
        divisions.reduce((acc, _, index) => {
          acc[index] = true
          return acc
        }, {})
      )
      setAllExpanded(true)
    }
  }, [searchText, divisions])

  const toggleDivision = (index: number) => {
    setOpenDivisions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  // Check for registration_id in URL
  useEffect(() => {
    if (divisions.length === 0) {
      return
    }
    const urlParams = new URLSearchParams(window.location.search)
    const registrationIdFromUrl = urlParams.get('registration_id')

    if (!registrationIdFromUrl) {
      return
    }

    // We need to find the division that has the registration_id, getting the index of the division
    const divisionIndexFromUrl = divisions.findIndex((d) =>
      d.teams?.some((t) =>
        t.registered_players?.some((p) => p.id === registrationIdFromUrl)
      )
    )

    if (divisionIndexFromUrl === -1) {
      return
    }

    // We need to get the team_roster_id for the registration_id
    const teamRosterIdFromUrl = divisions[divisionIndexFromUrl].teams?.find(
      (t) => t.registered_players?.some((p) => p.id === registrationIdFromUrl)
    )?.team_roster_id

    // Expand the row for the team_roster_id
    if (teamRosterIdFromUrl) {
      setExpandedRows((prev) => [...prev, teamRosterIdFromUrl])
    }

    // Now, we need to expand the division using toggleDivision
    toggleDivision(divisionIndexFromUrl)

    if (registrationIdFromUrl) {
      setRegistrationDetailDivision(divisions[divisionIndexFromUrl])
      setRegistrationDetailId(registrationIdFromUrl)
      setIsRegistrationDetailsModalOpen(true)
      // Remove the registration_id from URL without refreshing the page
      const newUrl = window.location.pathname
      window.history.replaceState({}, '', newUrl)
    }
  }, [divisions])

  // Check for team_registration_id in URL
  useEffect(() => {
    if (divisions.length === 0) {
      return
    }
    const urlParams = new URLSearchParams(window.location.search)
    const teamRegistrationIdFromUrl = urlParams.get('team_registration_id')

    if (!teamRegistrationIdFromUrl) {
      return
    }

    // We need to find the division that has the registration_id, getting the index of the division
    const divisionIndexFromUrl = divisions.findIndex((d) =>
      d.teams?.some((t) => t.id === teamRegistrationIdFromUrl)
    )

    if (divisionIndexFromUrl === -1) {
      return
    }

    // We need to get the team_roster_id for the registration_id
    const teamRosterIdFromUrl = divisions[divisionIndexFromUrl].teams?.find(
      (t) => t.id === teamRegistrationIdFromUrl
    )?.team_roster_id

    // Expand the row for the team_roster_id
    if (teamRosterIdFromUrl) {
      setExpandedRows((prev) => [...prev, teamRosterIdFromUrl])
    }

    // Now, we need to expand the division using toggleDivision
    toggleDivision(divisionIndexFromUrl)

    if (teamRegistrationIdFromUrl) {
      setRegistrationDetailId(teamRegistrationIdFromUrl)
      setIsRegistrationDetailsModalOpen(true)
      // Remove the team_registration_id from URL without refreshing the page
      const newUrl = window.location.pathname
      window.history.replaceState({}, '', newUrl)
    }
  }, [divisions])

  const onApprovedWithConditions = useCallback(
    async ({
      scheduleId: scheduleIdValue,
      scheduleRegistrationId: scheduleRegistrationIdValue,
      status: statusValue,
      teamRosterId: teamRosterIdValue,
      teamId: teamIdValue,
      conditions: conditionsValue,
      conditionType: conditionTypeValue,
      divisionId: divisionIdValue,
      team_registration_id: teamRegistrationIdValue,
      team: teamValue,
    }) => {
      // Remove try-catch wrapper - let errors bubble up to modal
      await updateTeamRegistrationStatus({
        scheduleId: scheduleIdValue,
        scheduleRegistrationId: scheduleRegistrationIdValue,
        status: statusValue,
        teamRosterId: teamRosterIdValue,
        teamId: teamIdValue,
        conditions: conditionsValue,
        conditionType: conditionTypeValue,
        divisionId: divisionIdValue,
        team_registration_id: teamRegistrationIdValue,
      })

      // Success logic remains the same
      setDivisions((prevDivisions) =>
        prevDivisions.map((div) => {
          if (div.id === teamValue?.division_id) {
            console.log(`found division ${div.id}`)
            return {
              ...div,
              teams: div.teams?.map((t) => {
                if (t.id === teamValue?.id) {
                  console.log(`found team ${t.id}`)
                  return {
                    ...t,
                    registration_status: Number(statusValue),
                  }
                }
                return t
              }),
            }
          }
          return div
        })
      )
      fetchAnalytics()
      const statusText = registrationOptions?.find(
        (option) => option.value === statusValue
      )?.label
      message.success(getUpdateMessage(teamValue?.name_full, statusText || ''))
    },
    [fetchAnalytics, updateTeamRegistrationStatus, message]
  )

  const onRegStatusChange = useCallback(
    async ({ value, team, division, refund_all_funds }) => {
      try {
        await updateTeamRegistrationStatus({
          scheduleId: scheduleId,
          status: value,
          conditions: null,
          conditionType: null,
          teamRegistrationId: team?.id,
          divisionId: division?.id,
          refund_all_funds: refund_all_funds,
        })
        // Update local state
        setDivisions((prevDivisions) =>
          prevDivisions.map((div) => {
            if (div.id === team?.division_id) {
              return {
                ...div,
                teams: div.teams?.map((t) => {
                  if (t.id === team?.id) {
                    return {
                      ...t,
                      registration_status: value,
                    }
                  }
                  return t
                }),
              }
            }
            return div
          })
        )
        fetchAnalytics()
        const teamName = team?.name
        const status = registrationOptions?.find(
          (option) => option.value === value
        )?.label
        message.success(getUpdateMessage(teamName, status || ''))

        // Refetch data to ensure consistency with server
        await refetch(false) // Don't show loading indicator for better UX
      } catch (e: any) {
        if (value === 5) {
          throw e
        }
        message.error(getErrorMessage(e))
        // On error, refetch to restore correct state
        refetch(false)
      }
    },
    [message, scheduleId, updateTeamRegistrationStatus, fetchAnalytics, refetch]
  )

  const onPlayerRegStatusChange = useCallback(
    async ({ value, player, division, refund_all_funds }) => {
      try {
        console.log(
          '🔄 onPlayerRegStatusChange',
          value,
          player,
          division,
          refund_all_funds
        )

        await updatePlayerRegistrationStatus({
          scheduleId: scheduleId,
          status: value,
          conditions: null,
          conditionType: null,
          divisionId: division?.id || null,
          teamRegistrationId: player?.id,
          refund_all_funds: refund_all_funds,
        })

        // Approved or approved with conditions
        const isApproved = value === 3 || value === 4

        // Cancelled
        const isCancelled = value === 5

        // Update local state
        setDivisions((prevDivisions) =>
          prevDivisions.map((div) => {
            if (div.id === division?.id) {
              return {
                ...div,
                teams: div.teams?.map((team) => {
                  if (team.team_roster_id === player?.team_roster_id) {
                    // Find the player in the current registered players to check previous status
                    const existingPlayer = team.registered_players?.find(
                      (p) =>
                        p.id === player?.id &&
                        p.team_roster_id === player.team_roster_id
                    )

                    return {
                      ...team,
                      ...(isApproved && {
                        players_count: team.players_count + 1,
                        roster_players_count: team.roster_players_count + 1,
                      }),
                      ...(isCancelled &&
                        existingPlayer?.registration_status === 3 && {
                          players_count: team.players_count - 1,
                          roster_players_count: team.roster_players_count - 1,
                        }),
                      registered_players: team.registered_players?.map((p) => {
                        if (
                          p.id === player?.id &&
                          p.team_roster_id === player.team_roster_id
                        ) {
                          return {
                            ...p,
                            registration_status: value,
                          }
                        }
                        return p
                      }),
                    }
                  }
                  return team
                }),
              }
            }
            return div
          })
        )
        fetchAnalytics()
        const playerName = player?.name || player?.name_full
        const status = registrationOptions.find(
          (option) => option.value === value
        )?.label
        message.success(getUpdateMessage(playerName, status || ''))
        // Refetch data to ensure consistency with server
        await refetch(false) // Don't show loading indicator for better UX
      } catch (e: any) {
        if (value === 5) {
          throw e
        }
        message.error(getErrorMessage(e))
        // On error, refetch to restore correct state
        refetch(false)
      }
    },
    [
      updatePlayerRegistrationStatus,
      scheduleId,
      setDivisions,
      fetchAnalytics,
      message,
      refetch,
    ]
  )

  const handlePaidInPersonConfirm = useCallback(async () => {
    if (!pendingStatusChange) {
      return
    }

    setIsPaidInPersonLoading(true)
    setPaidInPersonError(null)

    try {
      const { value, team, player, divisionId, entityType } =
        pendingStatusChange

      // Close the paid in person modal first
      setIsPaidInPersonAlertOpen(false)

      if (entityType === 'team') {
        const division = divisions.find((d) => d.id === team?.division_id)

        // If approve with conditions (value 4), open the side modal
        if (value === RegistrationStatus.APPROVED_WITH_CONDITIONS) {
          // Reset loading states
          setLoadingTeamIds((prev) => prev.filter((id) => id !== team?.id))

          // Open the conditions modal
          setTimeout(() => {
            openApproveWithConditionsModal(
              team,
              division?.id || '',
              division?.registration?.id || ''
            )
          }, 100) // Small delay to ensure modal transition
        } else {
          // Regular approval
          await onRegStatusChange({
            value,
            team,
            division,
            refund_all_funds: false,
          })
        }
      } else {
        const division = divisions.find((d) => d.id === divisionId)

        // If approve with conditions for player
        if (value === RegistrationStatus.APPROVED_WITH_CONDITIONS) {
          // Reset loading states
          setLoadingPlayerIds((prev) => prev.filter((id) => id !== player?.id))

          // Open the conditions modal
          setTimeout(() => {
            openApproveWithConditionsModal(
              player,
              division?.id || '',
              division?.registration?.id || ''
            )
          }, 100)
        } else {
          // Regular approval
          await onPlayerRegStatusChange({
            value,
            player: player?.player || player,
            division,
            refund_all_funds: false,
          })
        }
      }

      setPendingStatusChange(null)
    } catch (error: any) {
      setPaidInPersonError(error?.message || 'An error occurred')
      // Reopen the modal on error
      setIsPaidInPersonAlertOpen(true)
    } finally {
      setIsPaidInPersonLoading(false)
    }
  }, [
    pendingStatusChange,
    divisions,
    onRegStatusChange,
    onPlayerRegStatusChange,
    openApproveWithConditionsModal,
  ])

  const handleStatusChange = useCallback(
    async (value: number, teamId: string, team: any) => {
      try {
        const division = divisions.find((d) => d.id === team?.division_id)
        setCurrentDivision(division)
        setLoadingTeamIds((prev) => [...prev, teamId])
        const isApprovedStatus =
          value === RegistrationStatus.APPROVED ||
          value === RegistrationStatus.APPROVED_WITH_CONDITIONS
        const hasPaidInPerson = checkForDeferPayment(team)
        if (isApprovedStatus && hasPaidInPerson) {
          setPendingStatusChange({ value, teamId, team, entityType: 'team' })
          setIsPaidInPersonAlertOpen(true)
          return
        }
        // 5 is cancelled
        if (value === 5) {
          setTeamToCancel(team)
          setIsCancelModalOpen(true)
          // 4 is approved with conditions
        } else if (Number(value) === 4) {
          openApproveWithConditionsModal(
            team,
            division?.id || '',
            division?.registration?.id || ''
          )
        } else {
          onRegStatusChange({ value, team, division, refund_all_funds: false })
        }
      } catch (error: any) {
        message.error(error?.message)
      } finally {
        setLoadingTeamIds((prev) => prev.filter((id) => id !== teamId))
      }
    },
    [
      divisions,
      message,
      onRegStatusChange,
      scheduleId,
      openApproveWithConditionsModal,
    ]
  )

  const handlePlayerStatusChange = useCallback(
    async (
      value: number,
      playerId: string,
      player: any,
      divisionId: string
    ) => {
      try {
        const division = divisions.find((d) => d.id === divisionId)
        setCurrentDivision(division)
        setLoadingPlayerIds((prev) => [...prev, playerId])
        const isApprovedStatus =
          value === RegistrationStatus.APPROVED ||
          value === RegistrationStatus.APPROVED_WITH_CONDITIONS
        const hasPaidInPerson = checkForDeferPayment(player)
        if (isApprovedStatus && hasPaidInPerson) {
          setPendingStatusChange({
            value,
            playerId,
            player,
            divisionId,
            entityType: 'player',
          })
          setIsPaidInPersonAlertOpen(true)
          return
        }
        if (value === RegistrationStatus.CANCELLED) {
          setPlayerToCancel(player)
          setIsCancelModalOpen(true)
        } else if (
          Number(value) === RegistrationStatus.APPROVED_WITH_CONDITIONS
        ) {
          openApproveWithConditionsModal(
            player,
            division?.id || '',
            division?.registration?.id || ''
          )
          // Note: The modal will handle the status update and refetch
        } else {
          // First, find on the player billing schedule if the player is paid in person
          const isPaidInPerson = player?.billing_schedule?.find(
            (item) => item.defer_payment === true
          )
          if (isPaidInPerson) {
            // Update the player registration status to paid in person
          } else {
            await onPlayerRegStatusChange({
              value,
              player: player?.player || player,
              division,
              refund_all_funds: false,
            })
          }
        }
      } catch (error: any) {
        message.error(error?.message)
      } finally {
        setLoadingPlayerIds((prev) => prev.filter((id) => id !== playerId))
      }
    },
    [
      divisions,
      message,
      onPlayerRegStatusChange,
      scheduleId,
      openApproveWithConditionsModal,
    ]
  )

  const columns = useTableColumns({
    divisions,
    scheduleId,
    handleStatusChange,
    loadingTeamIds,
    setIsRegistrationDetailsModalOpen,
    setRegistrationDetailId,
    setRegistrationDetailDefaultTab,
    setRegistrationDetailDivision,
    setBucketToEdit,
    setBucketToDelete,
    setIsBucketModalOpen,
    setBucketScheduleRegistrationId,
    setIsPlayerAdminModalOpen,
    setParentTeamRegistrationId,
    setTeamId,
    setTeamName,
    setDivisionId,
    setDivision,
    setScheduleRegistrationId,
    isPlayerRegistrationEnabled,
  })

  const getColumnsBasedOnPaymentType = useCallback(
    (d: RegistrationTableDivision) => {
      if (d?.singlePayment) {
        return columns.filter((column) => column.key !== 'deposit_paid')
      }
      return columns
    },
    [columns]
  )

  const divisionsNotSet = useMemo(
    () => divisions.filter((d) => d?.registration === null),
    [divisions]
  )
  const hasOnlySeasonDivisions = useMemo(
    () => divisions.every((d) => d?.isSeason),
    [divisions]
  )

  const cancelRegistration = useCallback(
    async (refundAllFunds: boolean = false) => {
      try {
        setIsCancelling(true)
        setCancelModalError(null)
        await onRegStatusChange({
          value: 5,
          team: teamToCancel,
          division: null,
          refund_all_funds: refundAllFunds,
        })
        setIsCancelModalOpen(false)
        setTeamToCancel(null)
      } catch (e: any) {
        const errorMessage = getErrorMessage(e)
        setCancelModalError(errorMessage || 'Failed To Cancel Registration')
      } finally {
        setIsCancelling(false)
      }
    },
    [onRegStatusChange, teamToCancel]
  )

  const cancelPlayerRegistration = useCallback(
    async (refundAllFunds: boolean = false) => {
      try {
        setIsCancelling(true)
        setCancelModalError(null)
        await onPlayerRegStatusChange({
          value: 5,
          player: playerToCancel?.player,
          division: currentDivision,
          refund_all_funds: refundAllFunds,
        })
        setIsCancelModalOpen(false)
        setPlayerToCancel(null)
      } catch (e: any) {
        const errorMessage = getErrorMessage(e)
        setCancelModalError(errorMessage || 'Failed To Cancel Registration')
      } finally {
        setIsCancelling(false)
      }
    },
    [onPlayerRegStatusChange, playerToCancel, currentDivision]
  )

  return (
    <>
      <div
        css={css`
          display: flex;
          width: 100%;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          @media (max-width: 768px) {
            padding: 0px 16px;
          }
        `}
      >
        <SearchControls
          searchText={searchText}
          setSearchText={setSearchText}
          selectedDivision={selectedDivision}
          setSelectedDivision={setSelectedDivision}
          divisions={divisions}
          allExpanded={allExpanded}
          toggleAllDivisions={toggleAllDivisions}
          onlyBuckets={onlyBuckets}
          setOnlyBuckets={setOnlyBuckets}
          selectedStatuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          onlyDeferredPayments={onlyDeferredPayments}
          setOnlyDeferredPayments={setOnlyDeferredPayments}
        />
        {hasOnlySeasonDivisions && (
          <div
            css={css`
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            `}
          >
            <Alert
              message={
                <div>
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.PRIMARY}
                  >
                    You must have at least one division configured for
                    registration.{' '}
                    <Link
                      to={`${ROUTES.SCHEDULE_ROOT}/${scheduleId}/setup`}
                      css={css`
                        color: ${colors.PRIMARY};
                        text-decoration: underline;
                      `}
                    >
                      Click here to go to setup
                    </Link>{' '}
                    and create a division.
                  </Text>
                </div>
              }
              type='warning'
              showIcon
              css={css`
                width: 100%;
                font-size: 12px;
                font-weight: 400;
                line-height: 18px;
                color: ${colors.PRIMARY};
                border: 1px solid ${'#5B4810'};
                padding: 8px 16px;
                border-radius: 8px;
              `}
              icon={
                <WarningFilled
                  css={css`
                    color: ${colors.WARNING_300};
                    font-size: 16px;
                  `}
                />
              }
            />
          </div>
        )}
        {divisionsNotSet?.length > 0 && (
          <div
            css={css`
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            `}
          >
            <Alert
              message={
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                  `}
                >
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.PRIMARY_300}
                  >
                    There {divisionsNotSet?.length > 1 ? 'are' : 'is'}{' '}
                    {divisionsNotSet?.length}{' '}
                    {divisionsNotSet?.length > 1 ? 'divisions' : 'division'}{' '}
                    that has not been configured for registration. To configure{' '}
                    {divisionsNotSet?.length > 1 ? 'them' : 'it'}, click the
                    setup gear icon.
                  </Text>
                </div>
              }
              type='warning'
              showIcon
              css={css`
                width: 100%;
                font-size: 12px;
                font-weight: 400;
                line-height: 18px;
                color: ${colors.PRIMARY_300};
                border: 1px solid ${'#5B4810'};
                padding: 8px 16px;
                border-radius: 8px;
                margin-bottom: 8px;
              `}
              icon={
                <WarningFilled
                  css={css`
                    color: ${colors.WARNING_300};
                    font-size: 14px;
                  `}
                />
              }
            />
          </div>
        )}
        {!hasOnlySeasonDivisions && filteredDivisions.length > 0 ? (
          <>
            {visibleDivisions?.map((division, index) => (
              <DivisionTable
                key={index}
                division={division}
                index={index}
                openDivisions={openDivisions}
                toggleDivision={toggleDivision}
                onPressDivisionSetup={onPressDivisionSetup}
                setAdminTeamRegisterModalOpen={setAdminTeamRegisterModalOpen}
                setCurrentDivision={setCurrentDivision}
                columns={getColumnsBasedOnPaymentType(division)}
                getFilteredTeams={getFilteredTeams}
                getFilteredPlayers={getFilteredPlayers}
                teamToCancel={teamToCancel}
                onRegStatusChange={onRegStatusChange}
                message={message}
                DeleteBucketButton={DeleteBucketButton}
                currency={division?.currency}
                scheduleId={scheduleId}
                setDivisionToDelete={setDivisionToDelete}
                setIsDivisionCloneOpen={setIsDivisionCloneOpen}
                setDivisionToClone={setDivisionToClone}
                setIsRegistrationDetailsModalOpen={
                  setIsRegistrationDetailsModalOpen
                }
                setRegistrationDetailId={setRegistrationDetailId}
                setRegistrationDetailDivision={setRegistrationDetailDivision}
                setRegistrationDetailDefaultTab={
                  setRegistrationDetailDefaultTab
                }
                handlePlayerStatusChange={handlePlayerStatusChange}
                loadingPlayerIds={loadingPlayerIds}
                playerToCancel={playerToCancel}
                onPlayerRegStatusChange={onPlayerRegStatusChange}
                refetch={refetch}
                setIsBucketModalOpen={setIsBucketModalOpen}
                setBucketScheduleRegistrationId={
                  setBucketScheduleRegistrationId
                }
                allDivisions={divisions}
                setScheduleRegistrationId={setScheduleRegistrationId}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
                selectedPlayers={selectedPlayers}
                setSelectedPlayers={setSelectedPlayers}
                setIsManagePlayersModalOpen={setIsManagePlayersModalOpen}
                expandedRows={expandedRows}
                setExpandedRows={setExpandedRows}
                isPlayerRegistrationEnabled={isPlayerRegistrationEnabled}
                isTeamRegistrationEnabled={isTeamRegistrationEnabled}
                showWaiver={showWaiver}
              />
            ))}
            {hasMoreDivisions && (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  padding: 16px;
                  width: 100%;
                `}
              >
                <Button
                  onClick={handleLoadMore}
                  loading={isLoadingMore}
                  icon={isLoadingMore ? <LoadingOutlined /> : null}
                  css={css`
                    background: ${colors.SECONDARY_300};
                    color: ${colors.PRIMARY};
                    border: 1px solid ${colors.PRIMARY};
                    padding: 8px 24px;
                    height: 40px;
                    font-weight: 600;

                    &:hover {
                      background: ${colors.SECONDARY_200} !important;
                      color: ${colors.PRIMARY} !important;
                      border-color: ${colors.PRIMARY} !important;
                    }

                    &:focus {
                      background: ${colors.SECONDARY_200} !important;
                      color: ${colors.PRIMARY} !important;
                      border-color: ${colors.PRIMARY} !important;
                    }
                  `}
                >
                  Load More Divisions (
                  {filteredDivisions.length - visibleDivisions.length}{' '}
                  remaining)
                </Button>
              </div>
            )}
          </>
        ) : (
          <EmptySearch />
        )}
        <ApproveWithConditionsModal
          isOpen={isApproveWithConditionsModalOpen}
          onClose={closeApproveWithConditionsModal}
          onConfirm={onApprovedWithConditions}
          entity={approveWithConditionsEntity}
          divisionId={approveWithConditionsDivisionId}
          scheduleId={scheduleId}
          scheduleRegistrationId={approveWithConditionsScheduleRegistrationId}
          divisions={divisions}
          conditionType={conditionType}
          refetch={refetch}
        />
        <Suspense fallback={null}>
          <ManagePlayersModal
            isOpen={isManagePlayersModalOpen}
            onClose={() => setIsManagePlayersModalOpen(false)}
            selectedPlayers={selectedPlayers}
            onClearSelection={handleClearSelection}
            scheduleId={scheduleId}
            onSuccess={() => {
              setIsManagePlayersModalOpen(false)
              refetch()
            }}
            divisions={divisions}
          />
        </Suspense>
        <Suspense fallback={null}>
          <AdminPlayerRegisterModal
            isOpen={isPlayerAdminModalOpen}
            onClose={() => {
              setIsPlayerAdminModalOpen(false)
              setParentTeamRegistrationId(null)
              setTeamId(null)
              setDivisionId(null)
              setDivision(null)
            }}
            scheduleId={divisionId || ''}
            parentTeamRegistrationId={parentTeamRegistrationId || ''}
            teamId={teamId || ''}
            playerTypes={playerTypes || []}
            scheduleRegistrationId={scheduleRegistrationId || ''}
            countries={countries}
            refetch={refetch}
            organizationId={registration?.organization_id || ''}
            teamName={teamName || ''}
            division={division || ({} as RegistrationTableDivision)}
            currency={registration?.currency || ({} as Currency)}
            registrationVersion={registrationVersion}
            registrationVersionScheduleId={scheduleId}
            setRegistrationDetailId={setRegistrationDetailId}
            setIsRegistrationDetailsModalOpen={
              setIsRegistrationDetailsModalOpen
            }
          />
        </Suspense>
        <Suspense fallback={null}>
          <AdminTeamRegisterModal
            isOpen={adminTeamRegisterModalOpen}
            onClose={() => {
              setAdminTeamRegisterModalOpen(false)
              setParentTeamRegistrationId(null)
              setTeamId(null)
              setDivisionId(null)
            }}
            countries={countries}
            organizationId={registration?.organization_id || ''}
            scheduleId={scheduleId}
            scheduleRegistrationId={scheduleRegistrationId || ''}
            refetch={refetch}
            teamName={teamName || ''}
            teamOfficialTypes={teamOfficialTypes}
            sportId={registration?.sport_id || ''}
            division={currentDivision || ({} as RegistrationTableDivision)}
            currency={registration?.currency || ({} as Currency)}
            registrationVersion={registrationVersion}
          />
        </Suspense>
        <Suspense fallback={null}>
          <BillingScheduleModal
            isOpen={isBillingScheduleModalOpen}
            onClose={() => {
              setIsBillingScheduleModalOpen(false)
              setSelectedTeam(null)
              setSelectedPlayer(null)
              setSelectedPlayerTeamId(null)
            }}
            selectedBillingSchedule={
              selectedTeam || selectedPlayer || ({} as Team)
            }
            currencies={currencies}
            selectedPlayerTeamId={selectedPlayerTeamId}
          />
        </Suspense>
        <Suspense fallback={null}>
          <BucketModal
            isOpen={isBucketModalOpen}
            onClose={() => {
              setIsBucketModalOpen(false)
              setBucketToEdit(null)
              setBucketScheduleRegistrationId(null)
            }}
            scheduleId={scheduleId}
            registrationId={bucketScheduleRegistrationId || ''}
            refetch={refetch}
            bucketToEdit={bucketToEdit}
          />
        </Suspense>
        <CancelRegistrationModal
          isOpen={isCancelModalOpen && teamToCancel !== null}
          onClose={() => {
            setIsCancelModalOpen(false)
            setTeamToCancel(null)
            setCancelModalError(null)
          }}
          onConfirm={cancelRegistration}
          entityName={teamToCancel?.name || teamToCancel?.name_full || ''}
          entityType='team'
          isLoading={isCancelling}
          error={cancelModalError}
        />
        <CancelRegistrationModal
          isOpen={isCancelModalOpen && playerToCancel !== null}
          onClose={() => {
            setIsCancelModalOpen(false)
            setPlayerToCancel(null)
            setCancelModalError(null)
          }}
          onConfirm={cancelPlayerRegistration}
          entityName={
            playerToCancel?.player?.name ||
            playerToCancel?.player?.name_full ||
            ''
          }
          entityType='player'
          isLoading={isCancelling}
          error={cancelModalError}
        />

        <DeleteModal
          title={`Delete ${bucketToDelete?.name}?`}
          message={`Are you sure you want to delete ${bucketToDelete?.name}? \n This cannot be undone.`}
          onDelete={async (shouldForce = false) => {
            const query: any = {}
            if (shouldForce) {
              query.force = 1
            }
            try {
              await req(
                `/registration/${scheduleId}/admin/bucket/${bucketToDelete?.id}`,
                {
                  method: 'DELETE',
                  query,
                }
              )
              message.success(`${bucketToDelete?.name} deleted`)
              refetch(false)
            } catch (e: any) {
              message.error(e?.message)
              return false
            } finally {
              setBucketToDelete(null)
            }
          }}
          Button={DeleteBucketButton}
          useForceDelete={true}
          displayBanner={false}
          forceDeleteText=''
          shouldCloseModalAfterDelete={true}
        />
      </div>
      <Suspense fallback={null}>
        <PaidInPersonAlertModal
          isOpen={isPaidInPersonAlertOpen}
          onClose={() => {
            setIsPaidInPersonAlertOpen(false)
            setPendingStatusChange(null)
            setPaidInPersonError(null)
            setIsPaidInPersonLoading(false)
            // Reset loading states
            if (pendingStatusChange?.teamId) {
              setLoadingTeamIds((prev) =>
                prev.filter((id) => id !== pendingStatusChange.teamId)
              )
            }
            if (pendingStatusChange?.playerId) {
              setLoadingPlayerIds((prev) =>
                prev.filter((id) => id !== pendingStatusChange.playerId)
              )
            }
          }}
          onConfirm={handlePaidInPersonConfirm}
          entityName={
            pendingStatusChange?.team?.name ||
            pendingStatusChange?.player?.name ||
            pendingStatusChange?.player?.name_full ||
            'this registration'
          }
          entityType={pendingStatusChange?.entityType || 'team'}
          isLoading={isPaidInPersonLoading}
          error={paidInPersonError}
        />
      </Suspense>
    </>
  )
}

export default RegistrationTable
